import type { BlockKind, Connection, Database, DatabaseField, DatabaseRow, Page, PageBlock, Project, ProjectStatus, WorkspaceSnapshot } from './types';

const CACHE_KEY = 'agentico.workspace.cache.v2';

export class AuthRequired extends Error {
  constructor() { super('Authentication is required'); this.name = 'AuthRequired'; }
}

export class OfflineUnavailable extends Error {
  constructor() { super('The workspace is unavailable offline until it has been opened once.'); this.name = 'OfflineUnavailable'; }
}

export class ApiRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) { super(message); this.name = 'ApiRequestError'; }
}

type Raw = Record<string, unknown>;
type Bootstrap = { workspaces?: Raw[]; projects?: Raw[]; pages?: Raw[]; databases?: Raw[]; connections?: Raw[] };

function text(value: unknown, fallback = ''): string { return typeof value === 'string' ? value : fallback; }
function num(value: unknown, fallback = 0): number { return typeof value === 'number' ? value : Number(value) || fallback; }
function array(value: unknown): Raw[] { return Array.isArray(value) ? value.filter((item): item is Raw => typeof item === 'object' && item !== null) : []; }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: 'same-origin', ...init, headers: { 'content-type': 'application/json', ...init?.headers } });
  if (response.status === 401) throw new AuthRequired();
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: { message?: string; code?: string } } | null;
    throw new ApiRequestError(body?.error?.message ?? `Request failed (${response.status})`, response.status, body?.error?.code);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function projectStatus(value: unknown): ProjectStatus {
  return ({ active: 'On track', planned: 'Planning', paused: 'At risk', completed: 'Complete', archived: 'Complete' } as Record<string, ProjectStatus>)[text(value)] ?? 'Planning';
}

function mapProject(row: Raw): Project {
  const id = text(row.id);
  const fallbackProgress = row.status === 'completed' ? 100 : row.status === 'planned' ? 20 : 65;
  const planeProjectId = text(row.plane_project_id) || undefined;
  return { id, planeProjectId, title: text(row.name, 'Untitled project'), description: text(row.description), status: projectStatus(row.status), progress: row.progress === undefined || row.progress === null ? fallbackProgress : Math.max(0, Math.min(100, num(row.progress))), due: 'Ongoing', accent: text(row.color, '#6254d9'), members: ['LA'], version: num(row.version, 1) };
}

function blockKind(value: unknown): BlockKind {
  const kind = text(value);
  if (kind.startsWith('heading')) return 'heading';
  if (kind === 'todo') return 'check';
  if (kind === 'callout') return 'callout';
  return 'text';
}

function mapBlock(row: Raw): PageBlock {
  const content = (row.content_json ?? row.content ?? {}) as Raw;
  return { id: text(row.id, crypto.randomUUID()), kind: blockKind(row.block_type ?? row.kind), content: text(content.text ?? row.text ?? row.content), checked: Boolean(content.checked ?? row.checked) };
}

function updatedLabel(value: unknown): string {
  const date = new Date(text(value));
  if (Number.isNaN(date.getTime())) return 'Recently';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60_000));
  return minutes < 2 ? 'Just now' : minutes < 60 ? `${minutes}m ago` : minutes < 1440 ? `${Math.floor(minutes / 60)}h ago` : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function mapPage(row: Raw): Page {
  return { id: text(row.id), title: text(row.title, 'Untitled'), icon: text(row.icon, '◇'), updatedAt: updatedLabel(row.updated_at), projectId: text(row.project_id) || undefined, parentPageId: text(row.parent_page_id) || undefined, favorite: Boolean(row.is_favorite), blocks: array(row.blocks).map(mapBlock), version: num(row.version, 1) };
}

function fieldKind(value: unknown): DatabaseField['kind'] {
  const type = text(value);
  if (type === 'select' || type === 'multi_select') return 'status';
  if (type === 'person') return 'person';
  if (type === 'date') return 'date';
  if (type === 'number') return 'number';
  return 'text';
}

function mapDatabase(raw: Raw): Database {
  const properties = array(raw.properties);
  const fields = properties.map((item) => {
    const config = (item.config_json ?? item.config ?? {}) as Raw;
    const options = array(config.options).map((option) => text(option.name || option.id)).filter(Boolean);
    return { id: text(item.id), name: text(item.name, 'Field'), kind: fieldKind(item.property_type ?? item.type), type: text(item.property_type ?? item.type, 'text'), position: num(item.position), config, version: num(item.version, 1), options } satisfies DatabaseField;
  });
  const rows = array(raw.rows).map((item) => ({ id: text(item.id), planeProjectId: text(item.plane_project_id) || undefined, planeWorkItemId: text(item.plane_work_item_id) || undefined, pageId: text(item.page_id) || undefined, position: num(item.position), version: num(item.version, 1), values: (typeof item.values === 'object' && item.values ? item.values : {}) as Record<string, string | number> } satisfies DatabaseRow));
  const title = text(raw.name ?? raw.title, 'Database');
  const sourceKind = text(raw.source_kind) === 'plane' ? 'plane' : 'local';
  const views = array(raw.views).map((view) => ({ id: text(view.id), name: text(view.name, 'View'), type: (['table', 'board', 'list', 'calendar', 'gallery'].includes(text(view.view_type)) ? text(view.view_type) : 'table') as 'table' | 'board' | 'list' | 'calendar' | 'gallery', position: num(view.position), config: (typeof view.config_json === 'object' && view.config_json ? view.config_json : {}) as Record<string, unknown>, version: num(view.version, 1) }));
  return { id: text(raw.id), title, description: text(raw.description), projectId: text(raw.project_id) || undefined, pageId: text(raw.page_id) || undefined, sourceKind, readOnly: Boolean(raw.is_read_only) || sourceKind === 'plane' || title.toLowerCase() === 'plane progress', version: num(raw.version, 1), fields, rows, views };
}

function emptyDatabase(): Database { return { id: '', title: 'Work map', sourceKind: 'local', readOnly: false, fields: [], rows: [], views: [] }; }

function mapConnection(row: Raw): Connection {
  const rawState = text(row.state ?? row.status);
  const state: Connection['state'] = rawState === 'connected' ? 'connected' : rawState === 'error' || rawState === 'attention' ? 'attention' : 'offline';
  const scopes = row.scopes_json ?? row.scopes;
  return { id: text(row.id), name: text(row.name, 'Connection'), detail: text(row.detail, text(row.kind, 'MCP resource')), state, latency: text(row.latency) || undefined, resourceUrl: text(row.resourceUrl ?? row.resource_url) || undefined, scopes: Array.isArray(scopes) ? scopes.map(String) : [] };
}

let saveChains = new Map<string, Promise<unknown>>();
const serverVersions = new Map<string, number>();

async function fetchSnapshot(): Promise<WorkspaceSnapshot> {
  const selected = new URLSearchParams(window.location.search).get('workspaceId') || localStorage.getItem('agentico.workspaceId');
  const bootstrap = await request<Bootstrap>(`/api/bootstrap${selected ? `?workspaceId=${encodeURIComponent(selected)}` : ''}`);
  const pageRows = bootstrap.pages ?? [];
  const databaseRows = bootstrap.databases ?? [];
  const [pageDetails, databaseDetails] = await Promise.all([
    Promise.all(pageRows.map((page) => request<Raw>(`/api/pages/${encodeURIComponent(text(page.id))}`))),
    Promise.all(databaseRows.map((database) => request<Raw>(`/api/databases/${encodeURIComponent(text(database.id))}`))),
  ]);
  for (const page of pageDetails) serverVersions.set(text(page.id), num(page.version, 1));
  const workspaceId = text(bootstrap.workspaces?.[0]?.id, 'ws_agentico');
  localStorage.setItem('agentico.workspaceId', workspaceId);
  const databases = databaseDetails.map(mapDatabase);
  const requestedDatabaseId = new URLSearchParams(window.location.search).get('databaseId') || localStorage.getItem(`agentico.databaseId.${workspaceId}`);
  const selectedDatabase = databases.find((database) => database.id === requestedDatabaseId) ?? databases.find((database) => !database.readOnly) ?? databases[0] ?? emptyDatabase();
  if (selectedDatabase.id) localStorage.setItem(`agentico.databaseId.${workspaceId}`, selectedDatabase.id);
  const snapshot: WorkspaceSnapshot = {
    workspaceId,
    projects: (bootstrap.projects ?? []).map(mapProject),
    pages: pageDetails.map(mapPage),
    databases,
    database: selectedDatabase,
    planeDatabase: databases.find((database) => database.sourceKind === 'plane'),
    connections: (bootstrap.connections ?? []).map(mapConnection),
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot));
  return snapshot;
}

export const workspaceApi = {
  async load(): Promise<{ snapshot: WorkspaceSnapshot; offline: boolean }> {
    try { return { snapshot: await fetchSnapshot(), offline: false }; }
    catch (error) {
      if (error instanceof AuthRequired) throw error;
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) throw new OfflineUnavailable();
      const snapshot = JSON.parse(cached) as WorkspaceSnapshot;
      if (!Array.isArray(snapshot.databases)) snapshot.databases = snapshot.database ? [snapshot.database] : [];
      return { snapshot, offline: true };
    }
  },

  async savePage(page: Page, expectedVersion?: number): Promise<Page> {
    const previous = saveChains.get(page.id) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(() => request<Raw>(`/api/pages/${encodeURIComponent(page.id)}/content`, {
      method: 'PUT', keepalive: true, body: JSON.stringify({ title: page.title, icon: page.icon, blocks: page.blocks, expectedVersion: expectedVersion ?? serverVersions.get(page.id) ?? page.version ?? 1 }),
    })).then((raw) => { const saved = mapPage(raw); serverVersions.set(saved.id, saved.version ?? 1); return saved; });
    saveChains.set(page.id, next);
    return next;
  },

  async loadPage(pageId: string): Promise<Page> {
    const page = mapPage(await request<Raw>(`/api/pages/${encodeURIComponent(pageId)}`));
    serverVersions.set(page.id, page.version ?? 1);
    return page;
  },

  async capture(content: string, snapshot: WorkspaceSnapshot): Promise<WorkspaceSnapshot> {
    const page = await request<Raw>('/api/pages', { method: 'POST', body: JSON.stringify({ workspaceId: snapshot.workspaceId, title: content.slice(0, 64), icon: '↓' }) });
    const created = mapPage({ ...page, blocks: [] });
    const saved = mapPage(await request<Raw>(`/api/pages/${encodeURIComponent(created.id)}/content`, { method: 'PUT', body: JSON.stringify({ title: created.title, icon: created.icon, expectedVersion: created.version ?? 1, blocks: [{ id: crypto.randomUUID(), kind: 'text', content }] }) }));
    serverVersions.set(saved.id, saved.version ?? 1);
    const next = { ...snapshot, pages: [saved, ...snapshot.pages] };
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    return next;
  },

  async createPage(workspaceId: string, title = 'Untitled', options: { icon?: string; projectId?: string; parentPageId?: string } = {}): Promise<Page> {
    const raw = await request<Raw>('/api/pages', { method: 'POST', body: JSON.stringify({ workspaceId, title, icon: options.icon, projectId: options.projectId, parentPageId: options.parentPageId }) });
    const page = mapPage({ ...raw, blocks: [] });
    serverVersions.set(page.id, page.version ?? 1);
    return page;
  },

  async updatePageMetadata(page: Page, patch: { title?: string; icon?: string | null; projectId?: string | null; parentPageId?: string | null; favorite?: boolean }): Promise<Page> {
    const raw = await request<Raw>(`/api/pages/${encodeURIComponent(page.id)}`, { method: 'PATCH', body: JSON.stringify({ title: patch.title, icon: patch.icon, projectId: patch.projectId, parentPageId: patch.parentPageId, isFavorite: patch.favorite, expectedVersion: serverVersions.get(page.id) ?? page.version ?? 1 }) });
    const saved = { ...mapPage(raw), blocks: page.blocks };
    serverVersions.set(saved.id, saved.version ?? 1);
    return saved;
  },

  async loadConnections(): Promise<Connection[]> {
    const result = await request<{ connections?: Raw[] }>('/api/integrations/catalog');
    return (result.connections ?? []).map(mapConnection);
  },

  connect(id: string): void { window.location.assign(`/api/integrations/${encodeURIComponent(id)}/connect`); },

  async probe(id: string): Promise<Connection[]> {
    await request(`/api/integrations/${encodeURIComponent(id)}/probe`, { method: 'POST', body: '{}' });
    return this.loadConnections();
  },

  async addDatabaseRow(database: Database, values: Record<string, string | number>): Promise<Database> {
    await request(`/api/databases/${encodeURIComponent(database.id)}/rows`, { method: 'POST', body: JSON.stringify({ values }) });
    return mapDatabase(await request<Raw>(`/api/databases/${encodeURIComponent(database.id)}`));
  },

  async updateDatabaseRow(database: Database, row: DatabaseRow, values: Record<string, string | number>): Promise<Database> {
    if (database.readOnly) throw new Error('This database is managed by an integration and cannot be edited.');
    await request(`/api/rows/${encodeURIComponent(row.id)}`, { method: 'PATCH', body: JSON.stringify({ expectedVersion: row.version ?? 1, values }) });
    return this.loadDatabase(database.id);
  },

  async loadDatabase(databaseId: string): Promise<Database> {
    return mapDatabase(await request<Raw>(`/api/databases/${encodeURIComponent(databaseId)}`));
  },

  selectDatabase(snapshot: WorkspaceSnapshot, databaseId: string): WorkspaceSnapshot {
    const database = snapshot.databases.find((candidate) => candidate.id === databaseId);
    if (!database) throw new Error('Database not found in the current workspace.');
    localStorage.setItem(`agentico.databaseId.${snapshot.workspaceId}`, database.id);
    const next = { ...snapshot, database };
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    return next;
  },

  async createProject(workspaceId: string, name: string, description: string): Promise<WorkspaceSnapshot> {
    await request('/api/projects', { method: 'POST', body: JSON.stringify({ workspaceId, name, description, status: 'active' }) });
    return fetchSnapshot();
  },

  async createDatabase(workspaceId: string, name: string, description: string, options: { pageId?: string; projectId?: string } = {}): Promise<WorkspaceSnapshot> {
    const created = await request<Raw>('/api/databases', { method: 'POST', body: JSON.stringify({ workspaceId, name, description, pageId: options.pageId, projectId: options.projectId }) });
    localStorage.setItem(`agentico.databaseId.${workspaceId}`, text(created.id));
    return fetchSnapshot();
  },

  async addProperty(database: Database, name: string, type: string): Promise<Database> {
    await request(`/api/databases/${encodeURIComponent(database.id)}/properties`, { method: 'POST', body: JSON.stringify({ name, type }) });
    return mapDatabase(await request<Raw>(`/api/databases/${encodeURIComponent(database.id)}`));
  },

  async addView(database: Database, name: string, type: 'table' | 'board'): Promise<Database> {
    await request(`/api/databases/${encodeURIComponent(database.id)}/views`, { method: 'POST', body: JSON.stringify({ name, type }) });
    return mapDatabase(await request<Raw>(`/api/databases/${encodeURIComponent(database.id)}`));
  },

  async reconcilePlane(): Promise<WorkspaceSnapshot> {
    const workspaceId = new URLSearchParams(window.location.search).get('workspaceId') || localStorage.getItem('agentico.workspaceId');
    if (!workspaceId) throw new Error('Select a workspace before reconciling Plane.');
    await request('/api/integrations/plane/reconcile', { method: 'POST', body: JSON.stringify({ workspaceId }) });
    return fetchSnapshot();
  },

  refresh: fetchSnapshot,
};
